import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "../Navbar/navbar.css";
import logo from "../../../../acoustical-surfaces.svg";

// ─── Route label map ───────────────────────────────────────────────────────────
const ROUTE_LABELS = {
    "/": "Home",
    "/products": "Products",
    "/projects": "Projects",
    "/international": "International Sales",
    "/national": "National Sales",
    "/who-we-are": "Who We Are",
    "/soundproofingtips": "Soundproofing Tips",
    "/cad-library-guide": "CAD CSI Revit Library",
    "/literature": "Literature",
    "/links": "Links",
    "/downloads": "Downloads",
    "/contact-us": "Contact",
};

// ─── Desktop / Mobile nav items ────────────────────────────────────────────────
const NAV_ITEMS = [
    { label: "Home", to: "/" },
    { label: "Products", to: "/products" },
    { label: "Projects", to: "/projects" },
    {
        label: "Sales & Distribution",
        children: [
            { label: "International Sales", to: "/international" },
            { label: "National Sales", to: "/national" },
        ],
    },
    { label: "Who We Are", to: "/who-we-are" },
    {
        label: "Resources",
        children: [
            { label: "Soundproofing Tips", to: "/soundproofingtips" },
            { label: "CAD CSI Revit Library", to: "/cad-library-guide" },
            { label: "Literature", to: "/literature" },
            { label: "Links", to: "/links" },
            {
                label: "PQD",
                to: "https://www.acousticalsurfaces.in/pdfs/ASI-PQD-final%20-com.pdf",
                external: true,
            },
        ],
    },
    { label: "Downloads", to: "/downloads" },
    { label: "Contact", to: "/contact-us" },
];

// ─── Offcanvas sidebar sections (Key Products + Product Line) ──────────────────
const SIDEBAR_SECTIONS = [
    {
        heading: "Key Products",
        items: [
            { label: "FabriSorb", to: "/fabrisorb" },
            { label: "FabriWALL", to: "/fabriwall" },
            { label: "D'ecopanel", to: "/decopanel" },
            { label: "WoodTEC", to: "/woodtec" },
            { label: "Envirocoustic™ Wood Wool", to: "/envirocoustic-wood-wool" },
            { label: "FOAM STOP™ Polyurethane", to: "/foam-stop-polyurethane" },
            { label: "Poly Max™", to: "/poly-max" },
            { label: "Polysorb", to: "/polysorb" },
            { label: "Acoustic Doors", to: "/acoustic-doors" },
            { label: "Echo Eliminator™", to: "/echo-eliminator" },
            { label: "Noise STOP™ Vinyl Barrier", to: "/noise-stop-vinyl-barrier" },
            { label: "Decowood", to: "/decowood" },
            { label: "Diffuso", to: "/diffuso" },
            { label: "Polyster Ceiling Tiles", to: "/polyster-ceiling-tiles" },
            { label: "Ecotherm", to: "/ecotherm" },
            { label: "Knauf Danoline Kraft", to: "/knauf-danoline-kraft" },
            { label: "Sound Barrier", to: "/sound-barrier" },
        ],
    },
    {
        heading: "Product Line",
        items: [
            { label: "Absorptive Quilted Curtains", to: "/absorptive-quilted-curtains" },
            { label: "Acoustical Foam Panels", to: "/acoustical-foam-panels" },
            { label: "Acoustic Underlayment", to: "/acoustic-underlayment" },
            { label: "Acoustic Windows", to: "/acoustic-windows" },
            { label: "Adjustable Jamb Seals", to: "/adjustable-jamb-seals" },
            { label: "Barrier – Decoupler", to: "/barrier-decoupler" },
            { label: "Barrier ACT Ceiling Tiles", to: "/barrier-act-ceiling-tiles" },
            { label: "Barrier-Backed Ceiling Tiles", to: "/barrier-backed-ceiling-tiles" },
            { label: "CFAB Cellulose Panels", to: "/cfab-cellulose-panels" },
            { label: "Climate Seal Window Inserts", to: "/climate-seal-window-inserts" },
            { label: "Cotton Soundwave Eggcrate Panels", to: "/cotton-soundwave-eggcrate-panels" },
            { label: "Curve Panels", to: "/curve-panels" },
            { label: "dBa Ceiling Tiles", to: "/dba-ceiling-tiles" },
            { label: "Duracoustic", to: "/duracoustic" },
            { label: "Echo Eliminator Baffles", to: "/echo-eliminator-baffles" },
            { label: "Exterior Quilted Barrier/Absorbers", to: "/exterior-quilted-barrier-absorbers" },
            { label: "Glasswool Ceiling Tiles", to: "/glasswool-ceiling-tiles" },
            { label: "Green Glue Damping Compound", to: "/green-glue-damping-compound" },
            { label: "Heavy Duty Door Seal Kits", to: "/heavy-duty-door-seal-kits" },
        ],
    },
];

// ─── Component ─────────────────────────────────────────────────────────────────
function Navbar() {
    const location = useLocation();

    // Offcanvas open/close
    const [offcanvasOpen, setOffcanvasOpen] = useState(false);

    // Mobile breadcrumb dropdown open/close
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Mobile breadcrumb nested expand (tracks label string)
    const [mobileExpanded, setMobileExpanded] = useState(null);

    // Offcanvas nested expand (tracks label string, separate from mobile)
    // const [ocExpanded, setOcExpanded] = useState(null);

    // Refs for click-outside
    const mobileMenuRef = useRef(null);
    const offcanvasRef = useRef(null);

    const activeLabel = ROUTE_LABELS[location.pathname] || "Menu";

    // ── Close everything on route change ────────────────────────────────────────
    useEffect(() => {
        setMobileMenuOpen(false);
        setOffcanvasOpen(false);
        setMobileExpanded(null);
        // setOcExpanded(null);
    }, [location.pathname]);

    // ── Click-outside: mobile dropdown ──────────────────────────────────────────
    useEffect(() => {
        if (!mobileMenuOpen) return;
        function handler(e) {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
                setMobileMenuOpen(false);
                setMobileExpanded(null);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [mobileMenuOpen]);

    // ── Click-outside: offcanvas ─────────────────────────────────────────────────
    useEffect(() => {
        if (!offcanvasOpen) return;
        function handler(e) {
            if (
                offcanvasRef.current &&
                !offcanvasRef.current.contains(e.target) &&
                !e.target.closest(".asi-hamburger")
            ) {
                setOffcanvasOpen(false);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [offcanvasOpen]);

    // ── Helpers ──────────────────────────────────────────────────────────────────
    const closeAll = () => {
        setMobileMenuOpen(false);
        setOffcanvasOpen(false);
        setMobileExpanded(null);
        // setOcExpanded(null);
    };

    // ── Desktop nav item renderer ────────────────────────────────────────────────
    const renderDesktopItem = (item) => {
        if (item.children) {
            return (
                <li key={item.label} className="asi-menu-item asi-has-dropdown">
                    <span className="asi-menu-link asi-dropdown-trigger">
                        {item.label}
                        <svg className="asi-chevron" viewBox="0 0 10 6" width="10" height="6">
                            <path d="M0 0l5 6 5-6z" fill="currentColor" />
                        </svg>
                    </span>
                    <ul className="asi-dropdown">
                        {item.children.map((child) =>
                            child.external ? (
                                <li key={child.label}>
                                    <a href={child.to} className="asi-dropdown-item" target="_blank" rel="noopener noreferrer">
                                        {child.label}
                                    </a>
                                </li>
                            ) : (
                                <li key={child.label}>
                                    <Link to={child.to} className="asi-dropdown-item">{child.label}</Link>
                                </li>
                            )
                        )}
                    </ul>
                </li>
            );
        }
        return (
            <li key={item.label} className="asi-menu-item">
                <Link
                    to={item.to}
                    className={`asi-menu-link${location.pathname === item.to ? " asi-active" : ""}`}
                >
                    {item.label}
                </Link>
            </li>
        );
    };

    // ── Mobile breadcrumb item renderer ─────────────────────────────────────────
    const renderMobileItem = (item) => {
        if (item.children) {
            const isOpen = mobileExpanded === item.label;
            return (
                <li key={item.label} className="asi-mob-item asi-mob-has-children">
                    <button
                        className="asi-mob-group-btn"
                        onClick={() => setMobileExpanded((p) => (p === item.label ? null : item.label))}
                        aria-expanded={isOpen}
                    >
                        {item.label}
                        <svg className={`asi-mob-chevron${isOpen ? " asi-mob-chevron--open" : ""}`} viewBox="0 0 10 6" width="10" height="6">
                            <path d="M0 0l5 6 5-6z" fill="currentColor" />
                        </svg>
                    </button>
                    {isOpen && (
                        <ul className="asi-mob-sub">
                            {item.children.map((child) =>
                                child.external ? (
                                    <li key={child.label}>
                                        <a href={child.to} className="asi-mob-sub-link" target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                                            {child.label}
                                        </a>
                                    </li>
                                ) : (
                                    <li key={child.label}>
                                        <Link to={child.to} className="asi-mob-sub-link" onClick={closeAll}>{child.label}</Link>
                                    </li>
                                )
                            )}
                        </ul>
                    )}
                </li>
            );
        }
        return (
            <li key={item.label} className="asi-mob-item">
                <Link
                    to={item.to}
                    className={`asi-mob-link${location.pathname === item.to ? " asi-mob-active" : ""}`}
                    onClick={closeAll}
                >
                    {item.label}
                </Link>
            </li>
        );
    };

    // ── JSX ──────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* Overlay */}
            <div
                className={`asi-overlay${offcanvasOpen ? " asi-overlay--visible" : ""}`}
                onClick={() => setOffcanvasOpen(false)}
                aria-hidden="true"
            />

            <header className="asi-navbar">
                <div className="asi-navbar-inner">

                    {/* LOGO */}
                    <div className="asi-logo">
                        <Link to="/" onClick={closeAll}>
                            <img src={logo} alt="Acoustical Surfaces" />
                        </Link>
                    </div>

                    {/* DESKTOP MENU */}
                    <nav className="asi-desktop-nav" aria-label="Main navigation">
                        <ul className="asi-menu">
                            {NAV_ITEMS.map(renderDesktopItem)}
                        </ul>
                    </nav>

                    {/* MOBILE BREADCRUMB */}
                    <div className="asi-breadcrumb-wrap" ref={mobileMenuRef}>
                        <button
                            className="asi-breadcrumb-btn"
                            onClick={() => setMobileMenuOpen((p) => !p)}
                            aria-expanded={mobileMenuOpen}
                            aria-label="Toggle navigation menu"
                        >
                            {activeLabel}
                            <svg className={`asi-mob-chevron${mobileMenuOpen ? " asi-mob-chevron--open" : ""}`} viewBox="0 0 10 6" width="10" height="6">
                                <path d="M0 0l5 6 5-6z" fill="currentColor" />
                            </svg>
                        </button>

                        {mobileMenuOpen && (
                            <ul className="asi-mob-panel">
                                {NAV_ITEMS.map(renderMobileItem)}
                            </ul>
                        )}
                    </div>

                    {/* HAMBURGER */}
                    {/* <button
                        className={`asi-hamburger${offcanvasOpen ? " asi-hamburger--open" : ""}`}
                        onClick={() => setOffcanvasOpen((p) => !p)}
                        aria-label="Open side menu"
                        aria-expanded={offcanvasOpen}
                    >
                        <span className="asi-ham-line" />
                        <span className="asi-ham-line" />
                        <span className="asi-ham-line" />
                    </button> */}
                    <button
                        className="asi-products-btn"
                        onClick={() => setOffcanvasOpen((p) => !p)}
                        aria-expanded={offcanvasOpen}
                    >
                        OUR PRODUCTS
                    </button>
                </div>
            </header>

            {/* OFFCANVAS SIDEBAR */}
            <aside
                className={`asi-offcanvas${offcanvasOpen ? " asi-offcanvas--open" : ""}`}
                ref={offcanvasRef}
                aria-label="Side menu"
            >
                {/* Header */}
                <div className="asi-offcanvas-header">
                    <Link to="/" onClick={closeAll}>
                        <img src={logo} alt="Acoustical Surfaces" className="asi-offcanvas-logo" />
                    </Link>
                    <button className="asi-offcanvas-close" onClick={() => setOffcanvasOpen(false)} aria-label="Close menu">
                        ✕
                    </button>
                </div>

                {/* Sidebar body — scrollable */}
                <div className="asi-offcanvas-body">

                    {/* ── Main nav links ── */}
                    {/* <nav aria-label="Side navigation">
                        <ul className="asi-oc-main-list">
                            {NAV_ITEMS.map((item) => {
                                if (item.children) {
                                    const isOpen = ocExpanded === item.label;
                                    return (
                                        <li key={item.label} className="asi-oc-item asi-oc-has-children">
                                            <button
                                                className="asi-oc-group-btn"
                                                onClick={() => setOcExpanded((p) => (p === item.label ? null : item.label))}
                                                aria-expanded={isOpen}
                                            >
                                                {item.label}
                                                <svg className={`asi-mob-chevron${isOpen ? " asi-mob-chevron--open" : ""}`} viewBox="0 0 10 6" width="10" height="6">
                                                    <path d="M0 0l5 6 5-6z" fill="currentColor" />
                                                </svg>
                                            </button>
                                            {isOpen && (
                                                <ul className="asi-oc-sub">
                                                    {item.children.map((child) =>
                                                        child.external ? (
                                                            <li key={child.label}>
                                                                <a href={child.to} className="asi-oc-sub-link" target="_blank" rel="noopener noreferrer" onClick={closeAll}>
                                                                    {child.label}
                                                                </a>
                                                            </li>
                                                        ) : (
                                                            <li key={child.label}>
                                                                <Link to={child.to} className="asi-oc-sub-link" onClick={closeAll}>{child.label}</Link>
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                }
                                return (
                                    <li key={item.label} className="asi-oc-item">
                                        <Link
                                            to={item.to}
                                            className={`asi-oc-link${location.pathname === item.to ? " asi-oc-active" : ""}`}
                                            onClick={closeAll}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav> */}

                    {/* ── Product sections (Key Products + Product Line) ── */}
                    <div className="asi-oc-divider" />

                    {SIDEBAR_SECTIONS.map((section) => (
                        <div key={section.heading} className="asi-oc-section">
                            <h3 className="asi-oc-section-heading">{section.heading}</h3>
                            <ul className="asi-oc-section-list">
                                {section.items.map((item) => (
                                    <li key={item.label}>
                                        <Link to={item.to} className="asi-oc-section-link" onClick={closeAll}>
                                            {item.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                </div>
            </aside>
        </>
    );
}

export default Navbar;
